import moment from "moment";

// How the reconcile button pairs entries. Every mode needs the same amount;
// the others also check the dates, the descriptions, or both.
export const MATCH_MODES = [
  { value: "amount", label: "Amount only", date: false, description: false },
  { value: "amount_date", label: "Amount + Date", date: true, description: false },
  {
    value: "amount_description",
    label: "Amount + Description",
    date: false,
    description: true,
  },
  {
    value: "amount_date_description",
    label: "Amount + Date + Description",
    date: true,
    description: true,
  },
];

export const DATE_WINDOWS = [0, 1, 3, 7, 14, 30];

// dateWindow null is "None": no limit on how far apart the dates are, but
// the closest date is still paired first

export const DEFAULT_MATCH_SETTINGS = { mode: "amount_description", dateWindow: null };

// Descriptions count as alike from this score up (see descriptionSimilarity)
const DESCRIPTION_THRESHOLD = 0.5;

const DATE_FORMATS = [
  "D-MMM-YY",
  "D-MMM-YYYY",
  "D MMM YYYY",
  "YYYY-MM-DD",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD-MM-YYYY",
  moment.ISO_8601,
];

// Words that appear in most bank descriptions and say nothing about which
// transaction it is
const COMMON_WORDS = new Set([
  "the", "and", "for", "from", "with", "being", "via",
  "transfer", "trf", "trsf", "payment", "pymt", "credit", "debit",
  "branch", "entry", "deposit", "withdrawal", "cash",
]);

const cents = (value) => Math.round(Number(value || 0) * 100);

const parseDate = (value) => {
  if (!value) return null;
  const parsed = moment(String(value).trim(), DATE_FORMATS, true);
  return parsed.isValid() ? parsed.startOf("day") : null;
};

const dayGap = (a, b) => {
  const da = parseDate(a);
  const db = parseDate(b);
  if (!da || !db) return null;
  return Math.abs(da.diff(db, "days"));
};

const tokens = (description) =>
  String(description || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((t) => (/^\d+$/.test(t) ? t.replace(/^0+/, "") : t))
    .filter((t) => t.length >= 3 && !COMMON_WORDS.has(t));

// 0 to 1. A shared reference or cheque number (4+ digits) is 1 on its own;
// otherwise it is the share of the shorter description's words found in the
// other one.
export const descriptionSimilarity = (a, b) => {
  const ta = new Set(tokens(a));
  const tb = new Set(tokens(b));
  if (ta.size === 0 || tb.size === 0) return 0;
  let shared = 0;
  for (const t of ta) {
    if (!tb.has(t)) continue;
    if (/^\d{4,}$/.test(t)) return 1;
    shared += 1;
  }
  return shared / Math.min(ta.size, tb.size);
};

// Strong when the dates are within a day and the descriptions are alike,
// Medium when only one of those holds, Weak when neither does
const strengthOf = (gap, similarity) => {
  const closeDate = gap !== null && gap <= 1;
  const alike = similarity >= DESCRIPTION_THRESHOLD;
  if (closeDate && alike) return "Strong";
  if (closeDate || alike) return "Medium";
  return "Weak";
};

// Pairs entries in `left` with entries in `right` that have the same amount
// (`ids`) and pass the chosen mode's checks. Among the allowed pairs, the
// closest dates and most alike descriptions are paired first, so duplicate
// amounts go to their real counterparts rather than whichever comes first.
export function matchTransactions(left = [], right = [], settings = {}) {
  const { mode, dateWindow } = { ...DEFAULT_MATCH_SETTINGS, ...settings };
  const rules = MATCH_MODES.find((m) => m.value === mode) || MATCH_MODES[0];

  const rightByAmount = new Map();
  right.forEach((item, index) => {
    const key = cents(item.ids);
    if (!rightByAmount.has(key)) rightByAmount.set(key, []);
    rightByAmount.get(key).push(index);
  });

  const candidates = [];
  left.forEach((item, li) => {
    const same = rightByAmount.get(cents(item.ids)) || [];
    same.forEach((ri) => {
      const other = right[ri];
      const gap = dayGap(item.value_date, other.value_date);
      const similarity = descriptionSimilarity(item.description, other.description);

      if (rules.date && dateWindow !== null) {
        if (gap === null || gap > dateWindow) return;
      }
      if (rules.description && similarity < DESCRIPTION_THRESHOLD) return;

      const dateScore = gap === null ? 0 : 1 / (1 + gap);
      candidates.push({ li, ri, gap, similarity, score: dateScore + similarity });
    });
  });

  // Best pairs first; ties keep the statements' own order
  candidates.sort((a, b) => b.score - a.score || a.li - b.li || a.ri - b.ri);

  const usedLeft = new Set();
  const usedRight = new Set();
  const pairs = [];
  candidates.forEach(({ li, ri, gap, similarity }) => {
    if (usedLeft.has(li) || usedRight.has(ri)) return;
    usedLeft.add(li);
    usedRight.add(ri);
    pairs.push({
      left: left[li],
      right: right[ri],
      dayGap: gap,
      similarity,
      strength: strengthOf(gap, similarity),
    });
  });

  return {
    pairs,
    unmatchedLeft: left.filter((_, i) => !usedLeft.has(i)),
    unmatchedRight: right.filter((_, i) => !usedRight.has(i)),
  };
}

// The two rows the matched-items review shows for one pair, one per side
export function pairToReviewRows(pair, newId, sides) {
  const [leftSide, rightSide] = sides;
  const row = (item, other, side, otherSide) => ({
    id: newId(),
    value_date: item.value_date || other.value_date || "",
    description: item.description || other.description || "",
    amount: Number(item[side.amountKey] || item.ids || 0),
    source: side.label,
    matched_with: otherSide.label,
    source_description: item.description || "",
    matched_description: other.description || "",
    source_date: item.value_date || "",
    matched_date: other.value_date || "",
    strength: pair.strength,
  });
  return [
    row(pair.left, pair.right, leftSide, rightSide),
    row(pair.right, pair.left, rightSide, leftSide),
  ];
}

// Renamed when the default changed, so choices saved under the old default
// do not keep overriding it
const SETTINGS_KEY = "recon-match-settings-v3";

export const loadMatchSettings = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY));
    if (saved && MATCH_MODES.some((m) => m.value === saved.mode)) {
      return { ...DEFAULT_MATCH_SETTINGS, ...saved };
    }
  } catch {
    // Storage blocked or unreadable; fall back to the default
  }
  return DEFAULT_MATCH_SETTINGS;
};

export const saveMatchSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Not saved; the choice still applies until the page reloads
  }
};
